import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { PageHeader, Panel, Loading, Tag } from "@/components/common";
import { Brain, Plus, Check, X, Trash, Sparkle, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = ["studio_bible", "character", "world", "canon", "decision", "preference", "correction", "rule", "goal", "note"];
const SCOPES = ["studio", "series", "project", "user", "task"];

export default function AIMemory() {
  const { active } = useStudio();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "canon", title: "", content: "", scope: "studio" });

  const load = () => api.get("/memory").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, [active?.id]);

  const create = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title and content are required"); return; }
    try {
      await api.post("/memory", form);
      toast.success("Memory saved");
      setOpen(false);
      setForm({ category: "canon", title: "", content: "", scope: "studio" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const approve = async (id) => { await api.post(`/memory/${id}/approve`); toast.success("Approved — AI will now respect this"); load(); };
  const reject = async (id) => { await api.post(`/memory/${id}/reject`); toast("Rejected"); load(); };
  const remove = async (id) => { if (window.confirm("Delete this memory item?")) { await api.delete(`/memory/${id}`); toast.success("Deleted"); load(); } };

  if (!items) return <Loading />;
  const approved = items.filter((m) => m.status === "approved");
  const drafts = items.filter((m) => m.status === "draft");
  const rejected = items.filter((m) => m.status === "rejected");

  return (
    <div>
      <PageHeader icon={Brain} title="AI Memory & Canon"
        subtitle={`Persistent, approved knowledge for ${active.name}. Only APPROVED items are fed to the AI when it generates — drafts never become canon automatically.`}
        actions={<button data-testid="add-memory" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-magic px-4 py-2 text-sm font-semibold text-black"><Plus size={16} weight="bold" /> Add Memory</button>} />

      {drafts.length > 0 && (
        <div className="mb-8">
          <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><Sparkle size={18} weight="fill" className="text-[#FFB800]" /> Awaiting Approval ({drafts.length})</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {drafts.map((m) => (
              <MemoryCard key={m.id} m={m}
                actions={<>
                  <button data-testid={`approve-${m.id}`} onClick={() => approve(m.id)} className="inline-flex items-center gap-1 rounded-full border border-[#00FF9D]/40 text-[#00FF9D] px-3 py-1.5 text-xs"><Check size={13} /> Approve</button>
                  <button data-testid={`reject-${m.id}`} onClick={() => reject(m.id)} className="inline-flex items-center gap-1 rounded-full border border-white/10 text-zinc-300 px-3 py-1.5 text-xs"><X size={13} /> Reject</button>
                  <button data-testid={`del-mem-${m.id}`} onClick={() => remove(m.id)} className="inline-flex items-center gap-1 rounded-full border border-[#FF3366]/40 text-[#FF3366] px-3 py-1.5 text-xs"><Trash size={13} /></button>
                </>} />
            ))}
          </div>
        </div>
      )}

      <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><LockKey size={18} weight="fill" className="text-[#00FF9D]" /> Approved Canon ({approved.length})</h3>
      {approved.length === 0 ? <p className="text-sm text-zinc-500 mb-8">No approved memory yet. Add the studio's canon, characters, worlds and rules so the AI stays consistent.</p> : (
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {approved.map((m) => (
            <MemoryCard key={m.id} m={m}
              actions={<button data-testid={`del-mem-${m.id}`} onClick={() => remove(m.id)} className="inline-flex items-center gap-1 rounded-full border border-[#FF3366]/40 text-[#FF3366] px-3 py-1.5 text-xs"><Trash size={13} /> Remove</button>} />
          ))}
        </div>
      )}

      {rejected.length > 0 && (
        <div className="opacity-60">
          <h3 className="font-display text-base font-bold mb-3 text-zinc-400">Rejected ({rejected.length})</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {rejected.map((m) => (
              <MemoryCard key={m.id} m={m}
                actions={<button data-testid={`del-mem-${m.id}`} onClick={() => remove(m.id)} className="inline-flex items-center gap-1 rounded-full border border-[#FF3366]/40 text-[#FF3366] px-3 py-1.5 text-xs"><Trash size={13} /></button>} />
            ))}
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-lg glass rounded-2xl p-6 animate-fadeUp max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4"><h3 className="font-display text-xl font-bold">Add Memory / Canon</h3><button onClick={() => setOpen(false)}><X size={20} className="text-zinc-400" /></button></div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs text-zinc-500 mb-1.5">Category</div>
                  <select data-testid="mem-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full rounded-xl bg-black/30 border border-white/10 px-3 py-2.5 text-white [color-scheme:dark] capitalize">{CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}</select></div>
                <div><div className="text-xs text-zinc-500 mb-1.5">Scope</div>
                  <select data-testid="mem-scope" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className="w-full rounded-xl bg-black/30 border border-white/10 px-3 py-2.5 text-white [color-scheme:dark] capitalize">{SCOPES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
              </div>
              <input data-testid="mem-title" placeholder="Title (e.g. The Wayfinder's movement rules)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-2.5 text-white outline-none" />
              <textarea data-testid="mem-content" rows={5} placeholder="The fact the AI must always respect…" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-white outline-none resize-none" />
              <p className="text-xs text-zinc-500">As an owner/approver this is saved as approved canon immediately. Team members' entries are saved as drafts for your approval.</p>
            </div>
            <button data-testid="mem-create" onClick={create} className="mt-5 w-full rounded-full bg-magic py-2.5 font-semibold text-black">Save Memory</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MemoryCard({ m, actions }) {
  return (
    <Panel data-testid={`mem-${m.id}`} className="flex flex-col">
      <div className="flex items-center gap-2 mb-1.5">
        <Tag className="text-[10px] capitalize">{(m.category || "note").replace(/_/g, " ")}</Tag>
        <Tag className="text-[10px] text-zinc-400">{m.scope}</Tag>
      </div>
      <h4 className="font-display text-base font-bold text-white">{m.title}</h4>
      <p className="text-sm text-zinc-400 mt-1 flex-1 whitespace-pre-wrap break-words">{m.content}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">{actions}</div>
    </Panel>
  );
}
